import React, { useState, useMemo, useRef, useEffect } from "react";
import Button from "../Buttons/Button";
import CustomSelect from "../FormFields/Custom/CustomSelect";
import CustomSearch from "../FormFields/Custom/CustomSearch";
import Drawer from "../Modal/Drawer";
import { motion, Reorder } from "framer-motion";
import DropdownMenu from "../Buttons/DropdownMenu";
import CustomCheckboxNew from "../FormFields/Custom/CustomCheckboxNew";
import NoData from "../Info/NoData";

const NewTablePinSelected = ({
  rows,
  columns,
  fixedCol = 0,
  hideToolBar = false,
  tdClassName = "",
  toolbarRight = [],
  customSection,
  tableWidth = "w-full",
  showSlNo = false,
  serverPagination = false,
  totalCount = 0,
  page = 1,
  limit = 25,
  onPageChange,
  onLimitChange,
  showCheckbox = false,
  onSelectionChange,
  tableId = Math.random().toString(36).substring(2, 9),
  defaultSelectedRows = [],
  serverOnSearch,
  serverSerchValue,
  pinSelectedRows = false,
}) => {
  const options = [
    { label: "Showing 25 rows", value: "25" },
    { label: "Showing 50 rows", value: "50" },
    { label: "Showing 100 rows", value: "100" },
  ];

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [columnWidths, setColumnWidths] = useState({});
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const tableRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState(columns);
  const [internalLimit, setInternalLimit] = useState(limit);

  const getRowId = (row) => {
    if (row.id !== undefined) return String(row.id);
    if (row._id !== undefined) return String(row._id);
    return JSON.stringify(row);
  };

  const [selectedRows, setSelectedRows] = useState(() => {
    const initialSelection = new Set();
    defaultSelectedRows.forEach((row) => {
      const rowId = getRowId(row);
      initialSelection.add(rowId);
    });
    return initialSelection;
  });

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const newSelection = new Set();
      defaultSelectedRows.forEach((row) => {
        const rowId = getRowId(row);
        newSelection.add(rowId);
      });
      setSelectedRows(newSelection);
      if (onSelectionChange) {
        onSelectionChange(Array.from(newSelection));
      }
    }
  }, []);

  const rowsPerPage = serverPagination ? limit : internalLimit;

  const getSearchableText = (row, col) => {
    let value = "";
    if (col.valueGetter) {
      value = col.valueGetter(row[col.key], row);
    } else if (col.render) {
      const renderedValue = col.render(row);
      const extractText = (element) => {
        if (!element) return "";
        if (typeof element === "string") return element;
        if (Array.isArray(element)) return element.map(extractText).join(" ");
        if (React.isValidElement(element)) {
          if (element.props.children) {
            return extractText(element.props.children);
          }
          if (element.props) {
            return Object.values(element.props)
              .map((prop) => extractText(prop))
              .join(" ");
          }
          return "";
        }
        if (typeof element === "object") return JSON.stringify(element);
        return element.toString();
      };
      value = extractText(renderedValue);
    } else {
      value = row[col.key];
    }
    if (value instanceof Date) value = value.toLocaleDateString();
    if (typeof value === "object" && value !== null) {
      value = JSON.stringify(value);
    }
    return String(value ?? "").toLowerCase();
  };

  const rowsWithIndex = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        __originalIndex: index + 1,
      })),
    [rows]
  );

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rowsWithIndex;
    return [...rowsWithIndex].sort((a, b) => {
      const valueA = a[sortConfig.key] ?? "";
      const valueB = b[sortConfig.key] ?? "";
      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      } else {
        return sortConfig.direction === "asc"
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }
    });
  }, [rowsWithIndex, sortConfig]);

  const filteredRows = useMemo(() => {
    if (!search) return sortedRows;
    return sortedRows.filter((row) =>
      columns.some((col) =>
        getSearchableText(row, col).includes(search.toLowerCase())
      )
    );
  }, [search, sortedRows, columns]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  const { pinnedRows, regularRows } = useMemo(() => {
    if (!pinSelectedRows || !showCheckbox || serverPagination) {
      return { pinnedRows: [], regularRows: paginatedRows };
    }
    const pinned = [];
    const regular = [];
    paginatedRows.forEach((row) => {
      const rowId = getRowId(row);
      if (selectedRows.has(rowId)) {
        pinned.push({ ...row, __isPinned: true });
      } else {
        regular.push({ ...row, __isPinned: false });
      }
    });
    return { pinnedRows: pinned, regularRows: regular };
  }, [
    paginatedRows,
    selectedRows,
    pinSelectedRows,
    showCheckbox,
    serverPagination,
  ]);

  const displayedRows = serverPagination ? rows : paginatedRows;
  const currentCount = serverPagination ? totalCount : filteredRows.length;
  const currentLimit = serverPagination ? limit : rowsPerPage;
  const currentPageNumber = serverPagination ? page : currentPage;
  const totalPages = Math.ceil(currentCount / currentLimit);

  const areAllCurrentRowsSelected = useMemo(() => {
    if (displayedRows.length === 0) return false;
    return displayedRows.every((row) => selectedRows.has(getRowId(row)));
  }, [displayedRows, selectedRows]);

  const someRowsSelected = useMemo(() => {
    return (
      !areAllCurrentRowsSelected &&
      displayedRows.some((row) => selectedRows.has(getRowId(row)))
    );
  }, [displayedRows, selectedRows, areAllCurrentRowsSelected]);

  const handleSelectAllRows = (checked) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      displayedRows.forEach((row) => {
        newSelectedRows.add(getRowId(row));
      });
    } else {
      displayedRows.forEach((row) => {
        newSelectedRows.delete(getRowId(row));
      });
    }
    setSelectedRows(newSelectedRows);
    if (onSelectionChange) {
      onSelectionChange(Array.from(newSelectedRows));
    }
  };

  const handleSelectRow = (row, checked) => {
    const rowId = getRowId(row);
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(rowId);
    } else {
      newSelectedRows.delete(rowId);
    }
    setSelectedRows(newSelectedRows);
    if (onSelectionChange) {
      onSelectionChange(Array.from(newSelectedRows));
    }
  };

  const isRowSelected = (row) => {
    const rowId = getRowId(row);
    return selectedRows.has(rowId);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (tableRef.current) {
      const thElements = tableRef.current.querySelectorAll("thead th");
      const widths = {};
      thElements.forEach((th, index) => {
        if (columns[index]) {
          widths[columns[index].key] = th.getBoundingClientRect().width;
        }
      });
      setColumnWidths(widths);
    }
  }, [rows, columns]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // ✅ fixedCol logic uses colIndex
  const calculateLeftValue = (colIndex) => {
    let offset = 0;
    if (showCheckbox) offset += 40;
    if (showSlNo) offset += 80;
    return (
      offset +
      Object.keys(columnWidths)
        .slice(0, colIndex)
        .reduce((sum, key) => sum + columnWidths[key], 0)
    );
  };

  // ✅ unified cell renderer
  const renderCell = (col, row, colIndex, isPinned = false) => {
    const leftValue = calculateLeftValue(colIndex);
    return (
      <td
        key={col.key}
        className={`border-b-[1px] border-gray-300 px-4 py-2.5 text-sm text-nowrap leading-7 select-none 
          ${colIndex < fixedCol ? "sticky z-10" : ""} 
          ${isPinned ? "bg-info-50 group-hover:bg-info-100" : "bg-white"}
          ${tdClassName}`}
        style={colIndex < fixedCol ? { left: `${leftValue}px` } : {}}
      >
        {col.customCell
          ? col.customCell(row)
          : col.valueGetter
          ? col.valueGetter(row[col.key], row)
          : col.render
          ? col.render(row)
          : row[col.key] instanceof Date
          ? row[col.key].toLocaleDateString()
          : row[col.key] || "-"}
      </td>
    );
  };

  const renderPinnedRow = (row, rowIndex) => (
    <tr
      key={`pinned-${rowIndex}`}
      className="hover:shadow-lg transition-all duration-100 group  z-[35] hover:z-[36] sticky"
      style={{ top: `${48 + rowIndex * 41}px` }}
    >
      {showCheckbox && (
        <td
          className="border-b-[1px] border-gray-300 px-4 py-2.5 text-sm leading-7 select-none sticky z-10 bg-info-50 group-hover:bg-info-100"
          style={{ left: 0 }}
        >
          <div className="flex justify-center">
            <CustomCheckboxNew
              id={`pinned-row-checkbox-${tableId}-${rowIndex}`}
              checked={isRowSelected(row)}
              onChange={(e) => handleSelectRow(row, e.target.checked)}
            />
          </div>
        </td>
      )}
      {showSlNo && (
        <td
          className="border-b-[1px] border-gray-300 px-4 py-2.5 text-sm leading-7 select-none sticky z-10 bg-info-50 group-hover:bg-info-100"
          style={{ left: showCheckbox ? 40 : 0 }}
        >
          {filteredRows.findIndex((r) => getRowId(r) === getRowId(row)) + 1}
        </td>
      )}
      {visibleColumns
        .filter((col) => !col.hidden)
        .map((col, colIndex) => renderCell(col, row, colIndex, true))}
    </tr>
  );

  const renderRegularRow = (row, rowIndex) => (
    <tr
      key={`row-${rowIndex}`}
      className="hover:shadow-lg transition-all duration-100 group relative z-30 hover:z-[31]"
    >
      {showCheckbox && (
        <td
          className="border-b-[1px] border-gray-300 px-4 py-2.5 text-sm leading-7 select-none sticky z-10 bg-white"
          style={{ left: 0 }}
        >
          <div className="flex justify-center">
            <CustomCheckboxNew
              id={`row-checkbox-${tableId}-${rowIndex}`}
              checked={isRowSelected(row)}
              onChange={(e) => handleSelectRow(row, e.target.checked)}
            />
          </div>
        </td>
      )}
      {showSlNo && (
        <td
          className="border-b-[1px] border-gray-300 px-4 py-2.5 text-sm leading-7 select-none sticky z-10 bg-white"
          style={{ left: showCheckbox ? 40 : 0 }}
        >
          {serverPagination
            ? (currentPageNumber - 1) * currentLimit + rowIndex + 1
            : (currentPage - 1) * rowsPerPage + rowIndex + 1}
        </td>
      )}
      {visibleColumns
        .filter((col) => !col.hidden)
        .map((col, colIndex) => renderCell(col, row, colIndex, false))}
    </tr>
  );

  const handleColumnVisibilityChange = (columnKey) => {
    setVisibleColumns((prev) =>
      prev.map((col) =>
        col.key === columnKey ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleColumnReorder = (newOrder) => {
    setVisibleColumns(newOrder);
  };

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden ">
        {!hideToolBar && (
          <div className="tableToolBar flex justify-between items-center relative z-[48]">
            <div className="flex items-center py-2 gap-2">
              <div className="text-sm whitespace-nowrap">
                {currentCount > 0
                  ? `${(currentPageNumber - 1) * currentLimit + 1} - ${Math.min(
                      currentPageNumber * currentLimit,
                      currentCount
                    )} of ${currentCount}`
                  : "No results"}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="icon"
                  variant="text"
                  color="black"
                  icon="chevron_left"
                  onClick={() =>
                    serverPagination
                      ? onPageChange?.(currentPageNumber - 1)
                      : setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPageNumber === 1}
                  data-dropdown-persist="true"
                />
                <Button
                  icon="chevron_right"
                  type="icon"
                  variant="text"
                  color="black"
                  onClick={() =>
                    serverPagination
                      ? onPageChange?.(currentPageNumber + 1)
                      : setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={
                    currentPageNumber >= Math.ceil(currentCount / currentLimit)
                  }
                  data-dropdown-persist="true"
                />
                <div className="w-36">
                  <CustomSelect
                    size="sm"
                    placeholder="Select rows"
                    options={options}
                    selected={String(
                      serverPagination ? currentLimit : internalLimit
                    )}
                    onChange={(value) => {
                      const newLimit = Number(value);
                      if (serverPagination) {
                        onLimitChange?.(newLimit);
                      } else {
                        // Mirror the same behavior as handleLimitChange in AllClients
                        const currentStartIndex =
                          (currentPage - 1) * rowsPerPage + 1;
                        const newPage = Math.ceil(currentStartIndex / newLimit);
                        setCurrentPage(newPage);
                        setInternalLimit(newLimit);
                        onLimitChange?.(newLimit); // Optional: notify parent of change
                      }
                    }}
                    data-dropdown-persist="true"
                  />
                </div>
              </div>
              {showCheckbox && selectedRows.size > 0 && (
                <div className="text-xs ml-2">
                  {selectedRows.size}{" "}
                  {selectedRows.size === 1 ? "item" : "items"} selected
                </div>
              )}
            </div>

            <div>
              {/* Render the toolbar section unless `hideToolbar` is explicitly set to true */}
              {!toolbarRight.some((item) => item.hideToolbar) && (
                <div className="flex items-center gap-0.5">
                  {/* Render CustomSearch unless `customSearch` is explicitly set to false */}
                  {!toolbarRight.some(
                    (item) => item.customSearch === false
                  ) && (
                    <CustomSearch
                      size="sm"
                      //value={search}
                      value={serverPagination ? serverSerchValue : search}
                      // onChange={setSearch}
                      //onChange={(key, value) => setSearch(value)} // or just use keyname if needed
                      onChange={
                        serverPagination
                          ? serverOnSearch
                          : (key, value) => setSearch(value)
                      }
                      keyname="search" // Add this
                      rounded="rounded"
                      closeBtn
                    />
                  )}

                  {/* Render Filter Button unless `filterButton` is explicitly set to false */}
                  {!toolbarRight.some(
                    (item) => item.filterButton === false
                  ) && (
                    <Button
                      type="icon"
                      variant="text"
                      color="black"
                      icon="filter_alt"
                      onClick={() => setOpenFilterDrawer(true)}
                    />
                  )}

                  {/* Render DropdownMenu unless `DropdownMenu` is explicitly set to false */}
                  {!toolbarRight.some(
                    (item) => item.DropdownMenu === false
                  ) && (
                    <DropdownMenu
                      MenuSection={
                        <div className="flex flex-col">
                          <Button
                            title={"Option 1"}
                            variant="text"
                            color="black"
                            className="!justify-start py-1"
                          />
                          <Button
                            title={"Option 2"}
                            variant="text"
                            color="black"
                            className="!justify-start py-1"
                          />
                        </div>
                      }
                    >
                      <Button
                        icon="download"
                        type="icon"
                        variant="text"
                        color="black"
                      />
                    </DropdownMenu>
                  )}
                  {customSection}
                </div>
              )}
            </div>
          </div>
        )}

        <div
          className={`tableArea border-gray-300 border-[1px] border-b-0 rounded w-full m-auto overflow-auto ${tableWidth}  flex items-start flex-1`}
          ref={tableRef}
        >
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-primary-50 z-[40] sm:overflow-x-auto sm:overflow-y-hidden">
              <tr className="text-black border-b-[2px] border-gray-300">
                {showCheckbox && (
                  <th
                    className={`${
                      fixedCol > 0 ? "sticky bg-primary-50  z-[40]" : ""
                    }`}
                    style={fixedCol > 0 ? { left: 0 } : {}}
                  >
                    <div className="px-4 py-2.5 text-nowrap font-medium text-xs leading-4 select-none flex items-center justify-center">
                      <CustomCheckboxNew
                        id={`select-all-checkbox-${tableId}`}
                        checked={areAllCurrentRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={(e) => handleSelectAllRows(e.target.checked)}
                      />
                    </div>
                  </th>
                )}

                {showSlNo && (
                  <th
                    onClick={() => handleSort("__originalIndex")}
                    className={`${
                      fixedCol > 0 ? "sticky bg-primary-50  z-[40]" : ""
                    }`}
                    style={
                      fixedCol > 0 ? { left: showCheckbox ? "40px" : 0 } : {}
                    }
                  >
                    <div className="pl-4 pr-7 py-2.5 text-nowrap font-medium text-xs leading-4 select-none flex items-center justify-start gap-2 cursor-pointer">
                      <div>Sl No</div>
                      <span className="material-symbols-rounded !text-sm">
                        expand_all
                      </span>
                    </div>
                  </th>
                )}

                {visibleColumns
                  .filter((col) => !col.hidden)
                  .map((col, index) => {
                    const leftValue = calculateLeftValue(index);
                    return (
                      <th
                        key={col.key}
                        onClick={col.sortfun}
                        className={`${
                          index < fixedCol ? "sticky bg-primary-50 z-[40]" : ""
                        }`}
                        style={
                          index < fixedCol ? { left: `${leftValue}px` } : {}
                        }
                      >
                        <div
                          className={`pl-4 pr-7 py-2.5 text-nowrap font-medium text-xs leading-4 select-none flex items-center justify-start gap-2 ${
                            col.sortfun && "cursor-pointer"
                          }`}
                        >
                          <div>{col.label}</div>
                          {col.sortfun && (
                            <span className="material-symbols-rounded !text-sm">
                              expand_all
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
              </tr>
            </thead>

            <tbody>
              {/* Render pinned rows first - these will be sticky */}
              {pinSelectedRows &&
                showCheckbox &&
                !serverPagination &&
                pinnedRows.length > 0 && (
                  <>
                    {pinnedRows.map((row, rowIndex) => (
                      <React.Fragment key={`pinned-${rowIndex}`}>
                        <tr
                          className="hover:shadow-lg transition-all duration-100 group  z-[35] hover:z-[36] bg-info-50 hover:bg-info-100 sticky"
                          style={{
                            top: `${30 + rowIndex * 41}px`,
                          }}
                        >
                          {showCheckbox && (
                            <td
                              className={`border-b-[1px] border-gray-300 px-4 py-2.5 text-nowrap text-sm leading-7 select-none bg-info-50 group-hover:bg-info-100 ${
                                fixedCol > 0 ? "sticky z-10" : ""
                              }`}
                              style={fixedCol > 0 ? { left: 0 } : {}}
                            >
                              <div className="flex justify-center">
                                <CustomCheckboxNew
                                  id={`pinned-row-checkbox-${tableId}-${rowIndex}`}
                                  checked={isRowSelected(row)}
                                  onChange={(e) =>
                                    handleSelectRow(row, e.target.checked)
                                  }
                                />
                              </div>
                            </td>
                          )}

                          {showSlNo && (
                            <td
                              className={`border-b-[1px] border-gray-300 px-4 py-2.5 text-nowrap text-sm leading-7 select-none bg-info-50 group-hover:bg-info-100 ${
                                fixedCol > 0 ? "sticky z-10" : ""
                              }`}
                              style={
                                fixedCol > 0
                                  ? { left: showCheckbox ? "40px" : 0 }
                                  : {}
                              }
                            >
                              <div className="">
                                {/* Find original index in filtered rows for pinned row */}
                                {filteredRows.findIndex(
                                  (r) => getRowId(r) === getRowId(row)
                                ) + 1}
                              </div>
                            </td>
                          )}

                          {visibleColumns
                            .filter((col) => !col.hidden)
                            .map((col, colIndex) => (
                              <td
                                key={col.key}
                                className={`border-b-[2px] border-gray-200 px-4 py-2.5 text-nowrap text-sm leading-7 select-none bg-info-50 group-hover:bg-info-100 ${
                                  colIndex < fixedCol ? "sticky z-10" : ""
                                } ${tdClassName}`}
                                style={
                                  colIndex < fixedCol
                                    ? {
                                        left: `${calculateLeftValue(
                                          colIndex
                                        )}px`,
                                      }
                                    : {}
                                }
                              >
                                {col.customCell
                                  ? col.customCell(row)
                                  : col.valueGetter
                                  ? col.valueGetter(row[col.key], row)
                                  : col.render
                                  ? col.render(row)
                                  : row[col.key] instanceof Date
                                  ? row[col.key].toLocaleDateString()
                                  : row[col.key] || "-"}
                              </td>
                            ))}
                        </tr>
                      </React.Fragment>
                    ))}
                  </>
                )}

              {/* Render regular rows */}
              {(pinSelectedRows && showCheckbox && !serverPagination
                ? regularRows
                : displayedRows
              ).length > 0 ? (
                (pinSelectedRows && showCheckbox && !serverPagination
                  ? regularRows
                  : displayedRows
                ).map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <tr className="hover:shadow-lg transition-all duration-100 group relative z-30 hover:z-[31]">
                      {showCheckbox && (
                        <td
                          className={`border-b-[1px] border-gray-300 px-4 py-2.5 text-nowrap text-sm leading-7 select-none ${
                            fixedCol > 0 ? "sticky bg-white z-10" : ""
                          }`}
                          style={fixedCol > 0 ? { left: 0 } : {}}
                        >
                          <div className="flex justify-center">
                            <CustomCheckboxNew
                              id={`row-checkbox-${tableId}-${rowIndex}`}
                              checked={isRowSelected(row)}
                              onChange={(e) =>
                                handleSelectRow(row, e.target.checked)
                              }
                            />
                          </div>
                        </td>
                      )}

                      {showSlNo && (
                        <td
                          className={`border-b-[1px] border-gray-300 px-4 py-2.5 text-nowrap text-sm leading-7 select-none ${
                            fixedCol > 0 ? "sticky bg-white z-10" : ""
                          }`}
                          style={
                            fixedCol > 0
                              ? { left: showCheckbox ? "40px" : 0 }
                              : {}
                          }
                        >
                          <div className="">
                            {serverPagination
                              ? (currentPageNumber - 1) * currentLimit +
                                rowIndex +
                                1
                              : (currentPage - 1) * rowsPerPage + rowIndex + 1}
                          </div>
                        </td>
                      )}

                      {visibleColumns
                        .filter((col) => !col.hidden)
                        .map((col, colIndex) => renderCell(col, row, colIndex))}
                    </tr>
                    <div />
                  </React.Fragment>
                ))
              ) : (
                <NoData />
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Drawer
        title={"Column Management"}
        isOpen={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
        width="w-full max-w-xl"
      >
        <div className="p-0">
          <div>
            need check all/ uncheck all and reset reordr and uncheck allKeys
          </div>
          <Reorder.Group
            axis="y"
            values={visibleColumns}
            onReorder={handleColumnReorder}
            className="space-y-1"
          >
            {visibleColumns.map((column) => (
              <Reorder.Item
                key={column.key}
                value={column}
                className="bg-white rounded-lg shadow-sm"
              >
                <motion.div
                  className="flex items-center p-1 cursor-move"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="material-symbols-rounded mr-2">
                    drag_indicator
                  </span>
                  <label className="flex items-center flex-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!column.hidden}
                      onChange={() => handleColumnVisibilityChange(column.key)}
                      className="mr-2"
                    />
                    <span className="text-sm">{column.label}</span>
                  </label>
                </motion.div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        </div>
      </Drawer>
    </>
  );
};

export default NewTablePinSelected;
